import { Component, inject, TemplateRef, viewChild, signal, AfterViewInit } from '@angular/core';
import { AsyncPipe, CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { Observable, catchError, of, switchMap, map, combineLatest } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { AdmissionState, OrganizationListItem } from '../../../../../services/organizations/organization.model';
import { OrganizationService } from '../../../../../services/organizations/organization.service';
import { UseCaseParticipationService } from '../../../../../services/use-case-participation/use-case-participation.service';
import { UseCaseParticipationRequest } from '../../../../../services/use-case-participation/use-case-participation.model';
import { RepresentativeChangeService } from '../../../../../services/representative-change/representative-change.service';
import { RepresentativeChangeRequest } from '../../../../../services/representative-change/representative-change.model';
import { NavigationService } from '../../../../../services/navigation.service';

type RequestTabId = 'pending' | 'processed';

export interface UnifiedRequestItem {
  id: string;
  type: 'organization' | 'usecase' | 'representative-change';
  legalName: string;
  representativeName: string;
  admissionState: AdmissionState;
  createdDate: string;
  useCaseName?: string;
  newRepresentativeName?: string;
}

import { ProtectedContentComponent } from '../../../../../components/organisms/protected-content/protected-content.component';
import { NotificationService } from '../../../../../services/form/notification.service';
import { BadgeComponent, ButtonComponent, TableComponent, ColumnConfig, ColumnType, TabsComponent, TabItem } from "htx-ui-library";

@Component({
  selector: 'app-request-overview-page',
  imports: [CommonModule, AsyncPipe, ProtectedContentComponent, ButtonComponent, TableComponent, BadgeComponent, TranslocoPipe, TabsComponent],
  templateUrl: './request-overview-page.component.html',
  styleUrl: './request-overview-page.component.scss'
})
export class RequestOverviewPageComponent implements AfterViewInit {
  private router = inject(Router);
  private organizationService = inject(OrganizationService);
  private useCaseParticipationService = inject(UseCaseParticipationService);
  private representativeChangeService = inject(RepresentativeChangeService);
  private translocoService = inject(TranslocoService);
  private notificationService = inject(NotificationService);
  private navigationService = inject(NavigationService);

  requiredRoles = this.navigationService.getAccessRoles('admin/requests');

  typeTemplateRef = viewChild<TemplateRef<UnifiedRequestItem>>('columnType');
  statusTemplateRef = viewChild<TemplateRef<UnifiedRequestItem>>('columnStatus');
  actionsTemplateRef = viewChild<TemplateRef<UnifiedRequestItem>>('columnActions');

  private typeTemplate = signal<TemplateRef<UnifiedRequestItem> | undefined>(undefined);
  private statusTemplate = signal<TemplateRef<UnifiedRequestItem> | undefined>(undefined);
  private actionsTemplate = signal<TemplateRef<UnifiedRequestItem> | undefined>(undefined);
  private templatesReady = signal(false);

  activeTabId = signal<RequestTabId>('pending');

  private tabKeys = [
    { id: 'pending', labelKey: 'request.tabs.pending' },
    { id: 'processed', labelKey: 'request.tabs.processed' }
  ];

  tabs$: Observable<TabItem[]> = this.translocoService.selectTranslate([]).pipe(
    map(() =>
      this.tabKeys.map(tab => ({
        id: tab.id,
        label: this.translocoService.translate(tab.labelKey)
      }))
    )
  );

  allOrganizations$: Observable<OrganizationListItem[]> = this.organizationService.findAllOrganizations().pipe(
    catchError((error) => {
      console.log(error);
      this.notificationService.showError('organization.overview.loadingError', 5000);
      return of([]);
    })
  );

  allUseCaseRequests$: Observable<UseCaseParticipationRequest[]> = this.useCaseParticipationService.findAll().pipe(
    catchError((error) => {
      console.log(error);
      this.notificationService.showError('request.overview.loadingError', 5000);
      return of([]);
    })
  );

  allRepresentativeChangeRequests$: Observable<RepresentativeChangeRequest[]> = this.representativeChangeService.findAll().pipe(
    catchError((error) => {
      console.log(error);
      this.notificationService.showError('request.overview.loadingError', 5000);
      return of([]);
    })
  );

  private allUnified$: Observable<UnifiedRequestItem[]> = combineLatest([
    this.allOrganizations$,
    this.allUseCaseRequests$,
    this.allRepresentativeChangeRequests$
  ]).pipe(
    map(([organizations, useCaseRequests, repChangeRequests]) => {
      const orgItems: UnifiedRequestItem[] = organizations.map((org) => ({
        id: org.id,
        type: 'organization' as const,
        legalName: org.legalName,
        representativeName: org.representative
          ? `${org.representative.firstName} ${org.representative.lastName}`.trim()
          : '',
        admissionState: org.admissionState,
        createdDate: org.createdDate
      }));

      const useCaseItems: UnifiedRequestItem[] = useCaseRequests.map((req) => ({
        id: req.id,
        type: 'usecase' as const,
        legalName: req.organizationLegalName,
        representativeName: req.representativeName,
        admissionState: req.admissionState,
        createdDate: req.createdDate,
        useCaseName: req.useCaseName
      }));

      const repChangeItems: UnifiedRequestItem[] = repChangeRequests.map((req) => ({
        id: req.id,
        type: 'representative-change' as const,
        legalName: req.organizationLegalName,
        representativeName: req.oldRepresentativeName,
        admissionState: req.admissionState,
        createdDate: req.createdDate,
        newRepresentativeName: req.newRepresentativeName
      }));

      return [...orgItems, ...useCaseItems, ...repChangeItems];
    })
  );

  pendingRequests$: Observable<UnifiedRequestItem[]> = this.allUnified$.pipe(
    map((items) => items.filter((i) => i.admissionState === AdmissionState.PENDING))
  );

  processedRequests$: Observable<UnifiedRequestItem[]> = this.allUnified$.pipe(
    map((items) => items.filter((i) =>
      i.admissionState === AdmissionState.APPROVED || i.admissionState === AdmissionState.REJECTED
    ))
  );

  columnConfig$: Observable<ColumnConfig<UnifiedRequestItem>[]> = this.translocoService.selectTranslate([]).pipe(
    switchMap(() => {
      return new Observable<ColumnConfig<UnifiedRequestItem>[]>(observer => {
        const checkTemplates = () => {
          if (this.templatesReady()) {
            observer.next(this.buildColumnConfig());
            observer.complete();
          } else {
            setTimeout(checkTemplates, 50);
          }
        };
        checkTemplates();
      });
    })
  );

  private buildColumnConfig(): ColumnConfig<UnifiedRequestItem>[] {
    const typeTmpl = this.typeTemplate();
    const statusTmpl = this.statusTemplate();
    const actionsTmpl = this.actionsTemplate();

    return [
      {
        name: 'type',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('request.columns.type'),
        template: typeTmpl as any
      },
      {
        name: 'legalName',
        columnType: ColumnType.PLAIN,
        headerLabel: this.translocoService.translate('organization.control.organization.label'),
        cellContent: (rowData) => rowData.legalName,
      },
      {
        name: 'representativeName',
        columnType: ColumnType.PLAIN,
        headerLabel: this.translocoService.translate('organization.control.responsiblePerson.label'),
        cellContent: (rowData) => rowData.representativeName,
      },
      {
        name: 'createdDate',
        columnType: ColumnType.PLAIN,
        headerLabel: this.translocoService.translate('organization.control.registrationDate.label'),
        cellContent: (rowData) => rowData.createdDate ? new Date(rowData.createdDate).toLocaleDateString('de-DE') : '-',
      },
      {
        name: 'status',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('organization.control.admissionState.label'),
        template: statusTmpl as any,
      },
      {
        name: 'action',
        columnType: ColumnType.TEMPLATE,
        headerLabel: this.translocoService.translate('organization.control.action.label'),
        template: actionsTmpl as any
      }
    ];
  }

  ngAfterViewInit(): void {
    this.typeTemplate.set(this.typeTemplateRef());
    this.statusTemplate.set(this.statusTemplateRef());
    this.actionsTemplate.set(this.actionsTemplateRef());
    this.templatesReady.set(true);
  }

  goToDetail(item: UnifiedRequestItem): void {
    if (item.type === 'organization') {
      this.router.navigate(['admin', 'requests', item.id, 'approve']);
    } else if (item.type === 'usecase') {
      this.router.navigate(['admin', 'requests', 'use-case', item.id, 'approve']);
    } else if (item.type === 'representative-change') {
      this.router.navigate(['admin', 'requests', 'representative-change', item.id, 'approve']);
    }
  }

  getTyped(row: unknown) {
    return row as UnifiedRequestItem;
  }

  onTabChanged(tabId: string): void {
    if (tabId === 'pending' || tabId === 'processed') {
      this.activeTabId.set(tabId);
    }
  }

  protected readonly AdmissionState = AdmissionState;
}
